import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import { CnamApiDmpConnectJSNotAvailable } from "parog-web/utils/cnam-api-errors";

import type DmpConnectJSCardReaderAdapter from "parog-web/cnam-adapters/dmpconnect-js/card-reader-adapter";
import type DmpConnect from "parog-web/cnam-adapters/dmpconnect-js/dmp-connect";
import type PinCodeManager from "parog-web/cnam-adapters/dmpconnect-js/pin-code-manager";
import CnamServicesAccessFormAuthenticationSetPinCodeAndComputerSettingsComponent from "parog-web/components/cnam-services-access-form/authentication/set-pin-code-and-computer-settings";
import { State } from "parog-web/contexts/abstract/cnam-context";
import { AuthenticationContext } from "parog-web/contexts/authentication-context";
import type MedicModel from "parog-web/models/medic";
import type FetcherService from "parog-web/services/fetcher";
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";

export default class DmpConnectJSAuthenticationContextAdapter extends AuthenticationContext {
  declare pinCodeManager: PinCodeManager;
  declare cardReaderAdapter: DmpConnectJSCardReaderAdapter;
  declare dmpConnect: DmpConnect;
  declare medic: MedicModel;

  @tracked computerLocation?: string;
  @tracked dmpConnectJSIsAvailable?;

  @tracked sessionOpened = false;

  constructor(
    cnamAdapterName: string,
    pinCodeManager: PinCodeManager,
    cardReaderAdapter: DmpConnectJSCardReaderAdapter,
    dmpConnect: DmpConnect,
    medic: MedicModel,
    store: Store,
    fetcher: FetcherService,
    requestManager
  ) {
    super(
      cnamAdapterName,
      cardReaderAdapter,
      medic,
      store,
      fetcher,
      requestManager
    );

    this.pinCodeManager = pinCodeManager;
    this.cardReaderAdapter = cardReaderAdapter;
    this.dmpConnect = dmpConnect;
    this.medic = medic;
    this.reset();

    this.states = [
      new State(
        "check-installation-dmpconnect-js",
        (): Promise<boolean> => {
          return Promise.resolve(!this.dmpConnectJSIsAvailable);
        },
        async (): Promise<void> => {
          try {
            await this.dmpConnect.registerConfiguration();
            this.dmpConnectJSIsAvailable = true;
          } catch {
            this.dmpConnectJSIsAvailable = false;
            throw new CnamApiDmpConnectJSNotAvailable();
          }
        }
      ),
      new State(
        "open-session",
        (): Promise<boolean> => {
          return Promise.resolve(!this.sessionOpened);
        },
        async (): Promise<void> => {
          await this.dmpConnect.openSession();
          this.sessionOpened = true;
        }
      ),
      new State(
        "set-pin-code-and-computer-settings",
        (): Promise<boolean> => {
          return Promise.resolve(
            (!this.pinCodeManager.isStoredPinCodeValid ||
              !this.computerLocation) &&
              !this.dmpConnect.isRemoteMode
          );
        },
        async (): Promise<void> => {
          this.pinCodeManager.cachePinCode(this.pinCodeManager.typedPinCode!);
          setItemInLocalStorage(
            this._computerLocationKey(),
            this.computerLocation as string
          );
        },
        CnamServicesAccessFormAuthenticationSetPinCodeAndComputerSettingsComponent
      ),
      new State(
        "read-cps",
        (): Promise<boolean> => {
          return (
            Promise.resolve(!this.cpsInformations) ||
            !(
              Boolean(this.computerLocation) && Boolean(this.availableLocations)
            )
          );
        },
        async (): Promise<void> => {
          this.cpsInformations =
            await this.cardReaderAdapter.getCpsInformations();
          this.availableLocations =
            this.cpsInformations!.practiceLocations.filter(
              p => p.structureName && p.structureId
            );

          setItemInLocalStorage(
            this._availableLocationsKey(),
            JSON.stringify(this.availableLocations)
          );
        }
      ),
      ...this.states
    ];

    this.currentState = this.states.find(
      state => state.key == "check-installation-dmpconnect-js"
    ) as State;
  }

  reset(): void {
    super.reset();
    this.dmpConnectJSIsAvailable = false;
    this.sessionOpened = false;
    const computerLocationFromLocalStorage = getItemFromLocalStorage(
      this._computerLocationKey()
    );
    this.computerLocation =
      computerLocationFromLocalStorage == null
        ? undefined
        : computerLocationFromLocalStorage;

    this.currentState = this.states.find(
      state => state.key == "check-installation-dmpconnect-js"
    ) as State;
  }

  get isAuthenticated(): boolean {
    return (
      !this.updatePracticeLocation &&
      this.cpsInformations != undefined &&
      (this.pinCodeManager.isStoredPinCodeValid ||
        this.dmpConnect.isRemoteMode) &&
      ((this.cpsPracticeLocationIndice != undefined &&
        this.cpsPracticeLocationIndice >= 0 &&
        Boolean(this.practiceLocation) &&
        (Boolean(this.computerLocation) || this.dmpConnect.isRemoteMode) &&
        Boolean(this.availableLocations) &&
        this.ratesChecked) ||
        Boolean(this.forcedIdentifier))
    );
  }

  _computerLocationKey(): string {
    return "authentication:computer-location";
  }
}
